import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { fetchTokenFromLocalStorage } from 'utils/helperMethods';
import { APP_VERSION_RELOAD_EVENT_KEY, checkIfRefreshRequired, syncClientVersionFromApi } from 'utils/appVersion';

const REFRESH_MODAL_TITLE = 'New version available';
const REFRESH_MODAL_MESSAGE =
    'A new version of the app is available. Refreshing will reload this tab and all your other open app tabs to load the latest features and fixes. Your unsaved work may be lost if you have not saved.';

const VERSION_POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 hrs since last refresh

// Set this localStorage key (e.g. `localStorage.setItem('disableVersionCheck', 'true')`)
// to stop all version-check API calls — handy for local dev.
const DISABLE_VERSION_CHECK_KEY = 'disableVersionCheck';

const isVersionCheckDisabled = (): boolean => {
    try {
        return Boolean(localStorage.getItem(DISABLE_VERSION_CHECK_KEY));
    } catch {
        return false;
    }
};

/**
 * Handles app version sync on mount, version-check polling, and the "refresh required" modal.
 * Render once inside the main app (e.g. MainContainer) so it runs on any screen.
 */
const AppVersionRefresh = () => {
    const [showRefreshModal, setShowRefreshModal] = useState(false);
    const initialSyncDoneRef = useRef(false);

    const triggerRefresh = useCallback(() => {
        localStorage.setItem(APP_VERSION_RELOAD_EVENT_KEY, String(Date.now()));
        window.location.reload();
    }, []);

    // Sync app version from backend on every load (login already syncs; this covers refresh inside app).
    // Must complete before we run version-check poll so post-refresh we use the new client version and don't show the modal.
    useEffect(() => {
        if (isVersionCheckDisabled()) {
            initialSyncDoneRef.current = true;
            return;
        }
        const token = fetchTokenFromLocalStorage();
        const run = async () => {
            if (token) await syncClientVersionFromApi(token).catch(() => null);
            initialSyncDoneRef.current = true;
        };
        run();
    }, []);

    // Global app-layer version polling; when refresh required, show modal instead of auto-reload.
    useEffect(() => {
        const pollVersionAndShowModalIfNeeded = async () => {
            if (isVersionCheckDisabled()) return; // skip version-check when disabled via localStorage
            if (!initialSyncDoneRef.current) return; // wait until post-refresh sync has run so we have latest client version
            if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
            if (!localStorage.getItem('isAuthenticated')) return;

            try {
                const token = fetchTokenFromLocalStorage();
                const result = await checkIfRefreshRequired(token || undefined);
                if (!result?.refreshRequired) return;

                setShowRefreshModal(true);
            } catch {
                // Silent fail: poll is best-effort and should not interrupt users.
            }
        };

        const handleStorageReload = (event: StorageEvent) => {
            if (event.key === APP_VERSION_RELOAD_EVENT_KEY && event.newValue) {
                window.location.reload();
            }
        };

        const handleWindowActive = () => {
            pollVersionAndShowModalIfNeeded();
        };

        window.addEventListener('storage', handleStorageReload);
        window.addEventListener('focus', handleWindowActive);
        document.addEventListener('visibilitychange', handleWindowActive);

        pollVersionAndShowModalIfNeeded();
        const versionPollInterval = window.setInterval(pollVersionAndShowModalIfNeeded, VERSION_POLL_INTERVAL_MS);

        return () => {
            window.removeEventListener('storage', handleStorageReload);
            window.removeEventListener('focus', handleWindowActive);
            document.removeEventListener('visibilitychange', handleWindowActive);
            window.clearInterval(versionPollInterval);
        };
    }, []);

    return (
        <>
            <style>{`.app-version-refresh-modal .ant-modal-content { border: 2px solid #d9d9d9; border-radius: 8px; }`}</style>
            <Modal
                title={REFRESH_MODAL_TITLE}
                open={showRefreshModal}
                onCancel={() => setShowRefreshModal(false)}
                onOk={triggerRefresh}
                okText="Refresh now"
                cancelText="Defer"
                closable={false}
                maskClosable={false}
                className="app-version-refresh-modal"
                okButtonProps={{ className: 'bg-purpleFont border-2 border-purpleFont rounded-md' }}
            >
                <p className="text-fontColor mb-0">{REFRESH_MODAL_MESSAGE}</p>
            </Modal>
        </>
    );
};

export default AppVersionRefresh;
